import { notifications } from '@mantine/notifications';
import { IconCheck, IconExclamationCircle } from '@tabler/icons-react';
import { QueryClient, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Endpoints } from '@xver/apis';
import type { ActivityPage, ProjectErrorCode } from '@xver/apis/v1/projects/model';
import { blockStorageErrorText } from '@xver/utils/blockStorage';
import { getErrorCode, getErrorMessage } from '@xver/utils/error';
import { projectErrorText } from '@xver/utils/projects';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

export const PROJECT_DETAIL_QUERY_KEY = ['apis.v1.projects.detail'];
export const PROJECT_MEMBERS_QUERY_KEY = ['apis.v1.projects.members'];
export const PROJECT_INVITATIONS_QUERY_KEY = ['apis.v1.projects.invitations'];
export const PROJECT_RESOURCES_QUERY_KEY = ['apis.v1.projects.resources'];
export const PROJECT_ACTIVITY_QUERY_KEY = ['apis.v1.projects.activity'];

// Other members change these, so a cached or persisted copy is shown only until the refetch.
const SHARED_STALE_TIME = 0;

const RESOURCE_LIST_PREFIXES = [
    'api.v1.instances',
    'api.v1.baremetals',
    'apis.v1.kufer.containers',
    'api.v1.overview',
    'apis.v1.quotas',
];

/**
 * Refetches every `/v1/projects*` and `/v1/project-requests` query. Pass `withResources` after a
 * move or transfer, which also changes the VM, bare-metal, container, overview and quota lists.
 */
export function invalidateProjectQueries(
    queryClient: QueryClient,
    { withResources = false }: { withResources?: boolean } = {}
) {
    return queryClient.invalidateQueries({
        predicate: ({ queryKey }) => {
            const head = queryKey[0];
            if (typeof head !== 'string') return false;
            return (
                head.startsWith('apis.v1.project') ||
                (withResources && RESOURCE_LIST_PREFIXES.some((prefix) => head.startsWith(prefix)))
            );
        },
    });
}

export function useProjectQuery(projectId: string | undefined) {
    return useQuery({
        queryKey: [...PROJECT_DETAIL_QUERY_KEY, projectId],
        queryFn: () => Endpoints.v1.Projects.getProject(projectId as string),
        staleTime: SHARED_STALE_TIME,
        enabled: !!projectId,
        retry: (count, error) => getErrorCode(error) !== 'project_not_found' && count < 2,
    });
}

export function useProjectMembersQuery(projectId: string) {
    return useQuery({
        queryKey: [...PROJECT_MEMBERS_QUERY_KEY, projectId],
        queryFn: async () => (await Endpoints.v1.Projects.listMembers(projectId)).data ?? [],
        staleTime: SHARED_STALE_TIME,
    });
}

export function useProjectInvitationsQuery(projectId: string, enabled: boolean) {
    return useQuery({
        queryKey: [...PROJECT_INVITATIONS_QUERY_KEY, projectId],
        queryFn: async () => (await Endpoints.v1.Projects.listInvitations(projectId)).data ?? [],
        staleTime: SHARED_STALE_TIME,
        enabled,
    });
}

export function useProjectResourcesQuery(projectId: string) {
    return useQuery({
        queryKey: [...PROJECT_RESOURCES_QUERY_KEY, projectId],
        queryFn: () => Endpoints.v1.Projects.listResources(projectId),
        staleTime: SHARED_STALE_TIME,
    });
}

export function useProjectActivityQuery(projectId: string) {
    return useInfiniteQuery({
        queryKey: [...PROJECT_ACTIVITY_QUERY_KEY, projectId],
        queryFn: ({ pageParam }): Promise<ActivityPage> =>
            Endpoints.v1.Projects.listActivity(projectId, pageParam),
        initialPageParam: undefined as number | undefined,
        getNextPageParam: (last: ActivityPage) => last.nextCursor ?? undefined,
        staleTime: SHARED_STALE_TIME,
    });
}

const PROJECT_ERROR_CODES: ReadonlySet<string> = new Set<ProjectErrorCode>([
    'invalid_request',
    'confirm_name_mismatch',
    'payer_insufficient',
    'project_access_denied',
    'owner_only_source',
    'project_not_found',
    'resource_not_found',
    'member_not_found',
    'user_not_found',
    'invitation_not_found',
    'request_not_found',
    'default_project_immutable',
    'project_name_taken',
    'project_limit_reached',
    'project_not_empty',
    'owner_immutable',
    'already_member',
    'already_invited',
    'member_limit_reached',
    'owner_ineligible',
    'payer_ineligible',
    'resource_busy',
    'invite_rate_limited',
    'upstream_failed',
    'image_name_conflict',
    'payer_changed',
    'project_lookup_failed',
]);

/**
 * Toasts and localized error text for project mutations. Pass `{ self: true }` to `errorMessage`
 * when the viewer is the one taking the resources (accepting an ownership offer).
 */
export function useProjectFeedback() {
    const { t } = useTranslation('projects');
    const { t: tBlockStorage } = useTranslation('blockStorage');

    const errorMessage = useCallback(
        (error: unknown, { self }: { self?: boolean } = {}) => {
            const code = getErrorCode(error);
            const detail = error as { message?: string; reason?: unknown; limit?: unknown } | null;
            return (
                (code === 'block_storage_quota_exceeded' || code === 'block_volume_limit'
                    ? blockStorageErrorText(error, (key, values) =>
                          tBlockStorage(key as 'errors.block_volume_limit', values)
                      )
                    : undefined) ||
                projectErrorText(
                    code && PROJECT_ERROR_CODES.has(code) ? code : undefined,
                    detail?.message,
                    (key, options) => t(key as `errors.${ProjectErrorCode}`, options),
                    { reason: detail?.reason, limit: detail?.limit, self }
                ) ||
                getErrorMessage(error) ||
                t('errors.generic')
            );
        },
        [t, tBlockStorage]
    );

    const notifySuccess = useCallback((title: string, message: string) => {
        notifications.show({
            title,
            message,
            icon: <IconCheck size={18} color="#22c55e" aria-hidden="true" />,
        });
    }, []);

    const notifyError = useCallback(
        (error: unknown) => {
            notifications.show({
                title: t('toast.errorTitle'),
                message: errorMessage(error),
                icon: <IconExclamationCircle size={18} color="#eb5757" aria-hidden="true" />,
            });
        },
        [errorMessage, t]
    );

    return { errorMessage, notifySuccess, notifyError };
}
